import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

const PACKAGES = ["", "Start", "Build", "Scale", "Just exploring"];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        business_name: "",
        phone: "",
        package: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        try {
            const pkg = sessionStorage.getItem("sd_package");
            if (pkg) setForm((f) => ({ ...f, package: pkg }));
        } catch (_) {}
    }, []);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function onSubmit(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Name, email and message are required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/leads", {
                ...form,
                business_name: form.business_name || undefined,
                phone: form.phone || undefined,
                package: form.package || undefined,
            });
            setDone(true);
            toast.success("Got it. We'll be in touch within 24 hours.");
            setForm({ name: "", email: "", business_name: "", phone: "", package: "", message: "" });
            try {
                sessionStorage.removeItem("sd_package");
            } catch (_) {}
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section
            id="contact"
            className="relative border-b border-hairline bg-oat py-24 lg:py-36"
            data-testid="contact-section"
        >
            <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-coral">
                            // Say hi
                        </div>
                        <h2 className="font-display text-4xl font-black leading-[0.98] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                            Let&apos;s
                            <br />
                            <span className="font-serif italic font-normal text-coral">
                                make a plan.
                            </span>
                        </h2>
                        <p className="mt-6 max-w-md text-base leading-relaxed text-ink/75">
                            Tell us a bit about your business and what you&apos;re trying to crack.
                            We come back within 24 hours with a no-obligation game plan — even if
                            it&apos;s &ldquo;you don&apos;t need us yet.&rdquo;
                        </p>

                        <dl className="mt-10 space-y-5 border-t border-ink/15 pt-8 text-sm">
                            <div>
                                <dt className="text-xs uppercase tracking-widest text-ink/55">Email</dt>
                                <dd className="mt-1 font-display text-lg text-ink">
                                    <a
                                        href="mailto:office@simpledigital.co.nz"
                                        className="hover:text-coral"
                                        data-testid="contact-email"
                                    >
                                        office@simpledigital.co.nz
                                    </a>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs uppercase tracking-widest text-ink/55">Mobile</dt>
                                <dd className="mt-1 font-display text-lg text-ink">
                                    <a
                                        href="tel:+642108207461"
                                        className="hover:text-coral"
                                        data-testid="contact-mobile"
                                    >
                                        021 0820 7461
                                    </a>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs uppercase tracking-widest text-ink/55">Studio</dt>
                                <dd className="mt-1 font-display text-lg text-ink" data-testid="contact-studio">
                                    Onehunga · Tāmaki Makaurau · Auckland
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs uppercase tracking-widest text-ink/55">Hours</dt>
                                <dd className="mt-1 font-display text-lg text-ink" data-testid="contact-hours">
                                    Mon–Fri · 8:30am – 5pm
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className="lg:col-span-7">
                        {done ? (
                            <div
                                className="border-2 border-ink bg-sage p-10 text-center frame"
                                data-testid="contact-success"
                            >
                                <div className="font-display text-3xl font-black uppercase text-ink lg:text-5xl">
                                    Message sent.
                                </div>
                                <p className="mt-4 text-base text-ink/80">
                                    We&apos;ll be in your inbox within 24 hours. While you wait,
                                    follow us on Instagram —&nbsp;
                                    <span className="font-bold">@simpledigital.co</span>.
                                </p>
                                <button
                                    onClick={() => setDone(false)}
                                    className="mt-8 border-2 border-ink bg-paper px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-ink hover:bg-page hover:text-paper"
                                    data-testid="contact-send-another"
                                >
                                    Send another
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                className="border-2 border-ink/20 bg-paper p-6 lg:p-10"
                                data-testid="contact-form"
                            >
                                <div className="grid gap-5 lg:grid-cols-2">
                                    <Field label="Your name *">
                                        <input
                                            required
                                            value={form.name}
                                            onChange={update("name")}
                                            type="text"
                                            placeholder="Sam Tane"
                                            className={inputCls}
                                            data-testid="contact-input-name"
                                        />
                                    </Field>
                                    <Field label="Email *">
                                        <input
                                            required
                                            value={form.email}
                                            onChange={update("email")}
                                            type="email"
                                            placeholder="sam@yourshop.co"
                                            className={inputCls}
                                            data-testid="contact-input-email"
                                        />
                                    </Field>
                                    <Field label="Business name">
                                        <input
                                            value={form.business_name}
                                            onChange={update("business_name")}
                                            type="text"
                                            placeholder="Aroha & Co."
                                            className={inputCls}
                                            data-testid="contact-input-business"
                                        />
                                    </Field>
                                    <Field label="Phone (optional)">
                                        <input
                                            value={form.phone}
                                            onChange={update("phone")}
                                            type="tel"
                                            placeholder="+64 21 ..."
                                            className={inputCls}
                                            data-testid="contact-input-phone"
                                        />
                                    </Field>
                                    <Field label="Interested in" full>
                                        <select
                                            value={form.package}
                                            onChange={update("package")}
                                            className={`${inputCls} appearance-none`}
                                            data-testid="contact-select-package"
                                        >
                                            {PACKAGES.map((p) => (
                                                <option key={p || "any"} value={p} className="bg-paper text-ink">
                                                    {p || "Select a package…"}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="What's the brief? *" full>
                                        <textarea
                                            required
                                            value={form.message}
                                            onChange={update("message")}
                                            rows={5}
                                            placeholder="Tell us what you sell, where you sell it, and what's stuck."
                                            className={`${inputCls} resize-none`}
                                            data-testid="contact-input-message"
                                        />
                                    </Field>
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="mt-8 inline-flex w-full items-center justify-center gap-3 border-2 border-coral bg-coral px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:frame disabled:cursor-not-allowed disabled:opacity-60 lg:w-auto"
                                    data-testid="contact-submit"
                                >
                                    {submitting ? "Sending…" : "Send Message"}
                                    <Send className="h-4 w-4" />
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

const inputCls =
    "w-full border-2 border-ink/20 bg-transparent px-4 py-3 font-body text-base text-ink placeholder:text-ink/35 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30";

function Field({ label, children, full }) {
    return (
        <label className={`flex flex-col gap-2 ${full ? "lg:col-span-2" : ""}`}>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-ink/60">
                {label}
            </span>
            {children}
        </label>
    );
}
